import { formatDate, formatDateTime } from '@/lib/utils'
import type { CandidatePatient } from '@/types/deduplication'

/**
 * Decides one thing per field: does a human have to choose?
 *
 * The API reconciles both records itself and refuses the merge only where both hold a different,
 * non-empty value — so this detects that case rather than reproducing the API's rules. When the
 * preview endpoint lands, `getMergeResolution` becomes a call to it.
 */

export type MergeFieldSection = 'details' | 'address' | 'verification' | 'support' | 'history'

export type MergeFieldState =
  /** No primary picked yet — the outcome depends on which record survives. */
  | 'pending'
  /** Both records agree, or neither holds a value. */
  | 'same'
  /** Only one record holds a value; the API carries it over. */
  | 'auto'
  /** Both hold a different value and the API cannot choose. Blocks the merge. */
  | 'conflict'
  /** Medical history: both values kept and joined. */
  | 'combined'
  /** Carried, never cleared, not up for negotiation. */
  | 'locked'
  /** Follows another field's decision (the SCR date follows the SCR). */
  | 'paired'

export type MergeField = {
  /** The key sent in `fieldOverrides`. `address` is expanded into its columns on submit. */
  key: string
  label: string
  section: MergeFieldSection
  read: (patient: CandidatePatient) => string | null
  format?: (value: string) => string
  multiline?: boolean
  /**
   * `flag` — carried when either record has it, never cleared. `combine` — both values kept.
   * `paired` — travels with `pairedWith`. `address` — a block, and only if the post codes match.
   */
  policy?: 'flag' | 'combine' | 'paired' | 'address'
  pairedWith?: string
  /** A disagreement here is pre-answered with the primary's value instead of asked about. */
  defaultsToPrimary?: boolean
}

export const ADDRESS_COLUMNS = ['street', 'city', 'postal_code', 'county', 'country', 'address_note'] as const

export function isBlank(value: string | null | undefined): boolean {
  return value == null || value.trim() === ''
}

/** The API compares with `strcasecmp`, so "London" and "london" are not a conflict. */
function sameText(a: string | null, b: string | null): boolean {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase()
}

/**
 * Exact, because `handle_address_fields` compares the two stored post codes with `!==` and
 * refuses every address column unless they match character for character. Normalising spacing or
 * case here would hide a decision the API is about to demand — and calling it a conflict is always
 * recoverable, since picking a record sends all six columns and clears them.
 */
function samePostcode(a: CandidatePatient, b: CandidatePatient): boolean {
  const left = addressColumn(a, 'postal_code')
  return left !== null && left === addressColumn(b, 'postal_code')
}

export function addressColumn(patient: CandidatePatient, column: (typeof ADDRESS_COLUMNS)[number]): string | null {
  if (column === 'postal_code') return patient.address?.postal_code ?? patient.postal_code ?? null
  return patient.address?.[column] ?? null
}

export function formatAddress(patient: CandidatePatient): string | null {
  const parts = ADDRESS_COLUMNS.filter(column => column !== 'address_note')
    .map(column => addressColumn(patient, column))
    .filter((part): part is string => !isBlank(part))
  return parts.length > 0 ? parts.join(', ') : null
}

/**
 * The address the survivor ends up with when `chosen` is picked: that record wins column by
 * column, the other fills only the gaps. Shared with the request builder so the merged column
 * shows what is actually sent.
 */
export function resolveAddressColumns(
  chosen: CandidatePatient,
  fallback: CandidatePatient | undefined
): Record<(typeof ADDRESS_COLUMNS)[number], string | null> {
  const columns = {} as Record<(typeof ADDRESS_COLUMNS)[number], string | null>
  for (const column of ADDRESS_COLUMNS) {
    const value = addressColumn(chosen, column)
    const resolved = isBlank(value) && fallback ? addressColumn(fallback, column) : value
    columns[column] = isBlank(resolved) ? null : (resolved as string).trim()
  }
  return columns
}

function formatResolvedAddress(chosen: CandidatePatient, fallback: CandidatePatient | undefined): string | null {
  const columns = resolveAddressColumns(chosen, fallback)
  const parts = ADDRESS_COLUMNS.filter(column => column !== 'address_note')
    .map(column => columns[column])
    .filter((part): part is string => part !== null)
  return parts.length > 0 ? parts.join(', ') : null
}

export function parseSafeguarding(value: string | null): { question: string; answer: string }[] | null {
  if (!value) return null
  try {
    const parsed = JSON.parse(value) as Record<string, string>
    const entries = Object.entries(parsed)
    if (entries.length === 0) return null
    return entries.map(([question, answer]) => ({ question, answer }))
  } catch {
    return null
  }
}

/** Both records' values, de-duplicated, in column order. Used for medical history. */
export function combineValues(values: (string | null)[]): string | null {
  const present = values.filter((value): value is string => !isBlank(value)).map(value => value.trim())
  if (present.length === 0) return null
  return [...new Set(present)].join('\n\n')
}

export const MERGE_FIELDS: MergeField[] = [
  // The merged record is filed under the primary's name, so the two never need asking about.
  { key: 'first_name', label: 'First name', section: 'details', read: p => p.first_name, defaultsToPrimary: true },
  { key: 'last_name', label: 'Last name', section: 'details', read: p => p.last_name, defaultsToPrimary: true },
  { key: 'gender', label: 'Gender', section: 'details', read: p => p.gender },
  { key: 'date_of_birth', label: 'Date of birth', section: 'details', read: p => p.date_of_birth, format: formatDate },

  // The patient's own contact details, not the account holder's: the merge writes
  // `patient`.`mobile` / `patient`.`email`, while the `user` block further down belongs to the
  // login and is shown for context only. Left unformatted on purpose — the agent is picking the
  // exact string the merge writes, and normalising the display would make two different stored
  // values look identical while the row is flagged as a conflict.
  { key: 'mobile', label: 'Mobile', section: 'details', read: p => p.mobile },
  { key: 'email', label: 'Email', section: 'details', read: p => p.email },

  // One row, not six: a lone post code override leaves the survivor with the other one's street.
  { key: 'address', label: 'Address', section: 'address', read: formatAddress, multiline: true, policy: 'address' },

  {
    key: 'id_verified',
    label: 'ID verified',
    section: 'verification',
    read: p => (p.id_verified ? 'Yes' : 'No'),
    policy: 'flag'
  },
  { key: 'nhs_number', label: 'NHS number', section: 'verification', read: p => p.nhs_number },
  { key: 'idu_status', label: 'IDU status', section: 'verification', read: p => p.idu_status },
  {
    key: 'id_document_provided',
    label: 'ID document provided',
    section: 'verification',
    read: p => p.id_document_provided
  },
  {
    key: 'summary_care_record',
    label: 'Summary care record',
    section: 'verification',
    read: p => p.summary_care_record,
    multiline: true
  },
  {
    key: 'summary_care_record_updated_at',
    label: 'SCR updated at',
    section: 'verification',
    read: p => p.summary_care_record_updated_at,
    format: formatDateTime,
    policy: 'paired',
    pairedWith: 'summary_care_record'
  },

  { key: 'red_flag', label: 'Red flag', section: 'support', read: p => (p.red_flag ? 'Yes' : 'No'), policy: 'flag' },
  { key: 'red_flag_reason', label: 'Red flag reason', section: 'support', read: p => p.red_flag_reason },
  {
    key: 'safeguarding_questions',
    label: 'Safeguarding questions',
    section: 'support',
    read: p => p.safeguarding_questions,
    multiline: true
  },

  // `multiline` on all six: the combined value joins both records with a blank line between them,
  // which only reads as two entries if the cell keeps the line breaks.
  {
    key: 'medications',
    label: 'Medications',
    section: 'history',
    read: p => p.medications,
    multiline: true,
    policy: 'combine'
  },
  {
    key: 'allergies',
    label: 'Allergies',
    section: 'history',
    read: p => p.allergies,
    multiline: true,
    policy: 'combine'
  },
  {
    key: 'family_history',
    label: 'Family history',
    section: 'history',
    read: p => p.family_history,
    multiline: true,
    policy: 'combine'
  },
  {
    key: 'social_history',
    label: 'Social history',
    section: 'history',
    read: p => p.social_history,
    multiline: true,
    policy: 'combine'
  },
  {
    key: 'conditions',
    label: 'Conditions',
    section: 'history',
    read: p => p.conditions,
    multiline: true,
    policy: 'combine'
  },
  {
    key: 'other',
    label: 'Other observations',
    section: 'history',
    read: p => p.other,
    multiline: true,
    policy: 'combine'
  }
]

export type FieldChoice = { hash: string; label: string; value: string }

export type FieldResolution = {
  field: MergeField
  state: MergeFieldState
  /** One entry per displayed patient, in column order. */
  values: (string | null)[]
  /** What the merged patient ends up with before any override. */
  value: string | null
  /** Which record `value` came from — null when combined, empty, or undecided. */
  sourceHash: string | null
  reason: string | null
  /** The records a human can pick from (those holding a value). */
  choices: FieldChoice[]
  /** Whether picking a record is meaningful at all. */
  overridable: boolean
}

function patientLabel(index: number): string {
  return `Patient ${index + 1}`
}

/**
 * Fields the API compares but gives no way to resolve — surfaced up front instead of as a 400
 * after the agent has done the work.
 */
export type MergeBlocker = { label: string; detail: string }

export function getMergeBlockers(patients: CandidatePatient[]): MergeBlocker[] {
  if (patients.length < 2) return []
  const [a, b] = patients
  const blockers: MergeBlocker[] = []

  if (!isBlank(a.gp_name) && !isBlank(b.gp_name) && !sameText(a.gp_name, b.gp_name)) {
    blockers.push({
      label: 'Different GPs',
      detail:
        `${a.gp_name} on Patient 1, ${b.gp_name} on Patient 2. The merge API compares the GP but ` +
        'has no way to accept a choice, so it will refuse this merge. Set both records to the same ' +
        'GP (or clear one) on the patient page, then come back.'
    })
  }

  return blockers
}

/**
 * `patients` is in display order; `primaryHash` is the record that survives. The result is
 * direction-dependent — the survivor's value wins the API's fallback — so nothing resolves until
 * a primary is picked.
 */
export function getMergeResolution(patients: CandidatePatient[], primaryHash: string | null): FieldResolution[] {
  const keep = primaryHash ? patients.find(p => p.hash === primaryHash) : undefined
  const other = keep ? patients.find(p => p.hash !== keep.hash) : undefined

  const resolutions = MERGE_FIELDS.map<FieldResolution>(field => {
    const values = patients.map(patient => {
      const value = field.read(patient)
      return isBlank(value) ? null : (value as string).trim()
    })

    const choices = patients
      .map((patient, index) =>
        values[index] ? { hash: patient.hash, label: patientLabel(index), value: values[index] as string } : null
      )
      .filter((choice): choice is FieldChoice => choice !== null)

    const base = { field, values, choices, overridable: false }

    if (!keep || !other) return { ...base, state: 'pending', value: null, sourceHash: null, reason: null }

    const keepLabel = patientLabel(patients.indexOf(keep))
    const otherLabel = patientLabel(patients.indexOf(other))

    if (field.policy === 'flag') {
      const carriers = patients.filter(patient => field.read(patient) === 'Yes')
      if (carriers.length === 0) {
        return { ...base, state: 'same', value: 'No', sourceHash: null, reason: null }
      }
      return {
        ...base,
        state: 'locked',
        value: 'Yes',
        sourceHash: carriers[0].hash,
        reason:
          carriers.length === patients.length
            ? 'Set on both records.'
            : `Carried from ${patientLabel(patients.indexOf(carriers[0]))}. A merge never clears it.`
      }
    }

    const keepValue = values[patients.indexOf(keep)]
    const otherValue = values[patients.indexOf(other)]

    if (keepValue === null && otherValue === null) {
      return { ...base, state: 'same', value: null, sourceHash: null, reason: null }
    }
    if (sameText(keepValue, otherValue)) {
      return { ...base, state: 'same', value: keepValue, sourceHash: keep.hash, reason: null }
    }
    if (otherValue === null) {
      return { ...base, state: 'auto', value: keepValue, sourceHash: keep.hash, reason: `Only ${keepLabel} has one.` }
    }
    if (keepValue === null) {
      return {
        ...base,
        state: 'auto',
        value: otherValue,
        sourceHash: other.hash,
        reason: `Only ${otherLabel} has one — the API carries it over.`
      }
    }
    if (field.policy === 'combine') {
      return {
        ...base,
        overridable: true,
        state: 'combined',
        value: combineValues(values),
        sourceHash: null,
        reason: 'Both records are kept.'
      }
    }
    // Matching post codes are the API's own condition for merging the address; when they differ
    // it refuses every address column, so the whole address becomes one decision.
    if (field.policy === 'address' && samePostcode(keep, other)) {
      return {
        ...base,
        overridable: true,
        state: 'auto',
        value: keepValue,
        sourceHash: keep.hash,
        reason: `Post codes match, so the API keeps the most complete address. Pick a record to pin it to one.`
      }
    }
    return { ...base, overridable: true, state: 'conflict', value: null, sourceHash: null, reason: null }
  })

  // Paired fields travel with their partner, so the SCR and its date can never split.
  return resolutions.map(resolution => {
    if (resolution.field.policy !== 'paired' || resolution.state === 'pending') return resolution
    const partner = resolutions.find(candidate => candidate.field.key === resolution.field.pairedWith)
    if (!partner) return resolution
    return {
      ...resolution,
      state: 'paired',
      // The pass above may have called this a conflict (two different SCR dates), but there is
      // nothing to pick — the decision belongs to the partner.
      choices: [],
      overridable: false,
      reason: `Comes from whichever record supplies the ${partner.field.label.toLowerCase()} above, so the two can never split.`
    }
  })
}

/**
 * The choices a primary makes on the agent's behalf: the fields whose disagreement the survivor
 * already answers, pinned to it the moment it is picked. Returned as ordinary overrides so the
 * merged column, the dropdown and the request body all read as if the agent had picked them — and
 * so they can still be changed. Only genuine conflicts are seeded; anything the API can work out
 * alone stays out of the request.
 */
export function getPrimaryDefaultOverrides(patients: CandidatePatient[], primaryHash: string): Record<string, string> {
  const overrides: Record<string, string> = {}
  for (const resolution of getMergeResolution(patients, primaryHash)) {
    if (resolution.field.defaultsToPrimary && resolution.state === 'conflict') {
      overrides[resolution.field.key] = primaryHash
    }
  }
  return overrides
}

export type EffectiveField = FieldResolution & {
  /** Hash of the record the value comes from once the agent's choice is applied. */
  effectiveSource: string | null
  effectiveValue: string | null
  overridden: boolean
  /** A conflict nobody has resolved — the merge cannot be submitted while any of these exist. */
  unresolved: boolean
}

/** Layers the agent's choices (field key ⇒ patient hash) over the API's answer. */
export function applyOverrides(
  resolutions: FieldResolution[],
  overrides: Record<string, string>,
  patients: CandidatePatient[]
): EffectiveField[] {
  const byHash = new Map(patients.map(patient => [patient.hash, patient]))

  const resolve = (resolution: FieldResolution): EffectiveField => {
    const chosenHash = overrides[resolution.field.key]
    const chosen = chosenHash ? byHash.get(chosenHash) : undefined

    if (chosen) {
      // Picked as a block with the gaps filled, so show what will actually be written.
      const value =
        resolution.field.policy === 'address'
          ? formatResolvedAddress(
              chosen,
              patients.find(patient => patient.hash !== chosen.hash)
            )
          : resolution.field.read(chosen)
      return {
        ...resolution,
        effectiveSource: chosen.hash,
        effectiveValue: isBlank(value) ? null : (value as string).trim(),
        overridden: true,
        unresolved: false
      }
    }

    return {
      ...resolution,
      effectiveSource: resolution.sourceHash,
      effectiveValue: resolution.value,
      overridden: false,
      unresolved: resolution.state === 'conflict'
    }
  }

  const effective = resolutions.map(resolve)

  return effective.map(field => {
    if (field.field.policy !== 'paired') return field
    const partner = effective.find(candidate => candidate.field.key === field.field.pairedWith)
    if (!partner) return field
    const source = partner.effectiveSource ? byHash.get(partner.effectiveSource) : undefined
    const value = source ? field.field.read(source) : null
    return {
      ...field,
      effectiveSource: partner.effectiveSource,
      effectiveValue: isBlank(value) ? null : (value as string).trim(),
      overridden: partner.overridden,
      unresolved: false
    }
  })
}
